import { useCallback, useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'gallo_private_notifications';

export default function usePrivateNotifications({
  message,
  currentUser,
  activeRecipient,
  isPrivateChatOpen,
  onOpenConversation
}) {
  const [enabled, setEnabled] = useState(
    localStorage.getItem(STORAGE_KEY) === 'true'
  );

  const audioContextRef = useRef(null);
  const processedNotificationIdRef = useRef(null);
  const LOGO_URL = "https://media-manager-c.questera.ai/greta-media/786069decac80ebfda9758781ecad48f576fd486e338363dff55357567d6916b51c713fb933d539c7b707900e265a4cf/images/aW1hZ2UvcG5n/05487cee64958a73423d5bdd3c770274.png";

  const playSound = useCallback(() => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;

      const context = audioContextRef.current || new AudioContext();
      audioContextRef.current = context;

      const oscillator = context.createOscillator();
      const gain = context.createGain();

      oscillator.frequency.setValueAtTime(660, context.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(
        880,
        context.currentTime + 0.12
      );

      gain.gain.setValueAtTime(0.08, context.currentTime);
      gain.gain.exponentialRampToValueAtTime(
        0.001,
        context.currentTime + 0.2
      );

      oscillator.connect(gain);
      gain.connect(context.destination);

      oscillator.start();
      oscillator.stop(context.currentTime + 0.2);
    } catch {
      // El sonido es opcional y puede ser bloqueado por el navegador.
    }
  }, []);

  const toggleNotifications = useCallback(async () => {
    if (enabled) {
      setEnabled(false);
      localStorage.setItem(STORAGE_KEY, 'false');
      return;
    }

    try {
      if ('Notification' in window && Notification.permission === 'default') {
        await Notification.requestPermission();
      }
    } catch (error) {
      console.error('Error al solicitar permisos de notificación:', error);
    }

    setEnabled(true);
    localStorage.setItem(STORAGE_KEY, 'true');
    playSound();
  }, [enabled, playSound]);

  useEffect(() => {
    if (!message || message.id === processedNotificationIdRef.current) return;

    const isPrivateForCurrentUser =
      message.is_private &&
      message.recipient === currentUser &&
      message.nickname !== currentUser;

    if (isPrivateForCurrentUser && enabled) {
      const conversationVisible =
        isPrivateChatOpen &&
        activeRecipient === message.nickname &&
        document.visibilityState === 'visible';

      if (!conversationVisible) {
        playSound();

        if ('Notification' in window && Notification.permission === 'granted') {
          try {
            const notification = new Notification(
              `Mensaje privado de ${message.nickname}`,
              {
                body: message.content,
                icon: LOGO_URL,
                badge: LOGO_URL,
                tag: `private-${message.nickname}`,
                renotify: true
              }
            );

            notification.onclick = () => {
              window.focus();
              onOpenConversation(message.nickname);
              notification.close();
            };
          } catch (error) {
            if (navigator.serviceWorker) {
              navigator.serviceWorker.ready.then((registration) => {
                registration.showNotification(`Mensaje privado de ${message.nickname}`, {
                  body: message.content,
                  icon: LOGO_URL,
                  badge: LOGO_URL,
                  tag: `private-${message.nickname}`,
                  renotify: true
                });
              }).catch(() => {});
            }
          }
        }
      }
    }

    processedNotificationIdRef.current = message.id;
  }, [
    message,
    currentUser,
    activeRecipient,
    isPrivateChatOpen,
    enabled,
    onOpenConversation,
    playSound
  ]);

  return { notificationsEnabled: enabled, toggleNotifications };
}